import { cleanClass } from '@shared/utils/cleanClass.js';
import { forwardRef, useEffect, useRef } from 'react';

const ResizableTextArea = forwardRef(({ id, autoFocus, className, autoComplete, onFocus, onKeyUp, onBlur, disabled, type, placeholder, onChange, onKeyDown, value, size, borderless = false, style, autoSize, maxHeight, rows }, ref) => {
  const internalRef = useRef(null);
  const textareaRef = ref || internalRef;

  const handleKeyDown = (e) => {
    onKeyDown?.(e);
  };

  const handleKeyUp = (e) => {
    onKeyUp?.(e);
  };

  const adjustTextareaHeight = (textarea) => {
    if (!textarea) {
      return;
    }
    // Temporarily disable transition and reset height to measure true scrollHeight
    textarea.style.transition = 'none';
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    // Force a reflow so the browser registers the reset, then re-enable transition
    textarea.offsetHeight; // eslint-disable-line no-unused-expressions
    textarea.style.transition = '';
    if (maxHeight && newHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${newHeight}px`;
      textarea.style.overflowY = 'hidden';
    }
  };

  const handleChange = (e) => {
    if (type === 'textarea' && autoSize) {
      adjustTextareaHeight(e.target);
    }
    onChange?.(e);
  };

  useEffect(() => {
    if (autoSize && textareaRef?.current) {
      adjustTextareaHeight(textareaRef.current);
    }
  }, [autoSize, maxHeight, value]);

  useEffect(() => {
    if (autoSize && textareaRef?.current) {
      const timeoutId = setTimeout(() => {
        adjustTextareaHeight(textareaRef.current);
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  return (
    <textarea
      className={cleanClass(`comp-textarea ${size || ''} ${className || ''} ${borderless ? 'borderless' : ''}`)} //
      style={style}
      name={id}
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      onFocus={onFocus}
      onBlur={onBlur}
      disabled={disabled}
      placeholder={placeholder}
      spellCheck={true}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      value={value || ''}
      ref={textareaRef}
      rows={autoSize ? undefined : rows || 1}
    />
  );
});
ResizableTextArea.displayName = 'ResizableTextArea';

export default ResizableTextArea;
